import React, { useState, useEffect, useContext } from 'react'
import { Button } from 'react-bootstrap'

import authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { FilteredSearch } from 'plugin-codaf-risk-ext-bowtie/generic'
import { createOperation, deleteOperation, getOperations, updateOperation } from 'plugin-codaf-risk-ext-bowtie/services'
import { IOperation } from 'plugin-codaf-risk-ext-bowtie/types'
import { CustomList, RemoveConfirmationModal } from 'plugin-generic-components/shared/components/components'

import {
    BowtieBarrierSettingsRouteContext,
    IBowtieBarrierSettingsRouteContext,
    IQueryParamsList
} from '../../BowtieBarrierSettingWrapper'
import { CreateEditOperationModal } from './components/CreateEditOperationModal/CreateEditOperationModal'
import { OperationsContainer } from './styles'

export const Operations: React.FC = () => {
    const { params, setParams } = useContext<IBowtieBarrierSettingsRouteContext<IQueryParamsList>>(
        BowtieBarrierSettingsRouteContext
    )
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN

    const [loading, setLoading] = useState<boolean>(false)
    const [request, setRequest] = useState<{ total: number; totalPages: number; data: IOperation[] }>()
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
    const [showEditModal, setShowEditModal] = useState<boolean>(false)
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
    const [tempOperation, setTempOperation] = useState<IOperation>(null)
    const [operations, setOperations] = useState<IOperation[]>()
    const [total, setTotal] = useState<number>()
    // const [searchTerm, setSearchTerm] = useState<string>('')

    const handleGetOperations = async (): Promise<void> => {
        try {
            setLoading(true)
            const request = await getOperations({
                direction: params.direction,
                keyword: params.keyword,
                orderBy: params.orderBy,
                page: params.page,
                perPage: params.perPage
            })
            setRequest(request)
            setOperations(request.data)
            setTotal(request.total)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('Unable to fetch operations.'))
        } finally {
            setLoading(false)
        }
    }

    const handleCreateNewOperation = async (operationName: string): Promise<void> => {
        try {
            setLoading(true)
            const newOperation: Omit<IOperation, 'id'> = { description: operationName }
            await createOperation(newOperation)
            globalNotification.success(i18n('Operation created successfully.'))
            handleGetOperations()
        } catch (error) {
            globalNotification.error(error.message ?? i18n('Unable to create operation.'))
            setLoading(false)
        }
    }

    const handleEditOperation = async (operation: IOperation): Promise<void> => {
        try {
            setLoading(true)
            await updateOperation(operation)
            globalNotification.success(i18n('Operation updated successfully.'))
            handleGetOperations()
        } catch (error) {
            globalNotification.error(error.message ?? i18n('Unable to update operation.'))
            setLoading(false)
        }
    }

    // const generateTempOperationArray = (): IOperation[] => {
    //     return operations.filter(operation => operation.description.toLowerCase().includes(searchTerm))
    // }

    const handleSortList = async (orderBy: string, direction: 'ASC' | 'DESC'): Promise<void> => {
        setParams({ ...params, orderBy, direction })
    }

    const handleShowCreateModal = (): void => {
        setShowCreateModal(true)
    }

    const handleCloseProtocol = (): void => {
        setShowCreateModal(false)
        setShowEditModal(false)
        setShowDeleteModal(false)
        setTempOperation(null)
    }

    const handleShowEditModal = (operation: IOperation): void => {
        setShowEditModal(true)
        setTempOperation(operation)
    }

    const handleShowRemoveModal = (operation: IOperation): void => {
        setShowDeleteModal(true)
        setTempOperation(operation)
    }

    useEffect(() => {
        handleGetOperations()
    }, [params])

    const handleQuickSerach = (input: string): void => {
        setParams({ ...params, keyword: input })
    }

    return (
        <OperationsContainer>
            <div className="first-row-container">
                {bowtieAdmin && (
                    <Button
                        className="no-outline-button"
                        bsStyle="primary"
                        onClick={handleShowCreateModal}
                        style={{ borderRadius: 3, marginRight: 8 }}
                        id="diagramList-list-button-newDiagram"
                    >
                        <i className="far fa-plus custom-plus" /> {i18n('New Operation')}
                    </Button>
                )}
                <FilteredSearch
                    keyword={params.keyword}
                    inputId="diagramList-list-field-searchDiagram"
                    onSearch={handleQuickSerach}
                />
            </div>
            {loading ? (
                <div className="loading-spin">
                    <i className="fa fa-spinner fa-spin fa-x2" />
                </div>
            ) : operations?.length ? (
                <CustomList
                    label="Operations"
                    list={operations.map(
                        (el): IOperation => ({
                            id: el?.id ?? undefined,
                            description: el?.description ?? ''
                        })
                    )}
                    columns={[
                        { title: 'ID', sortable: true, listRef: 'id' },
                        { title: 'NAME', sortable: true, listRef: 'description' }
                    ]}
                    sortDirection={params.direction}
                    sortField={params.orderBy === 'id' ? 'id' : params.orderBy}
                    onSortClick={handleSortList}
                    tableProps={{ style: { width: '100%' } }}
                    configOptions={
                        bowtieAdmin
                            ? [
                                  { icon: 'far fa-edit', onClickConfig: o => handleShowEditModal(o) },

                                  { icon: 'far fa-trash-alt', onClickConfig: o => handleShowRemoveModal(o) }
                              ]
                            : [{ icon: 'fas fa-eye', onClickConfig: o => handleShowEditModal(o) }]
                    }
                    pagination={{
                        type: 'live',
                        totalItems: total,
                        showingItems: operations.length
                    }}
                    fixedHeader={true}
                />
            ) : (
                <div className="no-operation">
                    <i className="far fa-folder-open " />
                    <p>{i18n('There are no operations yet')}</p>
                    <a onClick={handleShowCreateModal}>{i18n('Create new operation')}</a>
                </div>
            )}
            <CreateEditOperationModal
                show={showCreateModal}
                setShow={setShowCreateModal}
                type={'create'}
                handleClose={handleCloseProtocol}
                handleCreate={handleCreateNewOperation}
            />
            <CreateEditOperationModal
                show={showEditModal}
                setShow={setShowEditModal}
                type={'edit'}
                handleClose={handleCloseProtocol}
                operation={tempOperation}
                setOperation={setTempOperation}
                handleUpdate={handleEditOperation}
            />
            <RemoveConfirmationModal
                setShow={setShowDeleteModal}
                show={showDeleteModal}
                handleClose={handleCloseProtocol}
                type={'operation'}
                removalMessage={`${i18n('Are you sure you want remove the operation')} ${tempOperation?.description}?`}
                onDelete={async () => {
                    await deleteOperation(tempOperation.id)
                    handleGetOperations()
                }}
            />
        </OperationsContainer>
    )
}
